import { Controller } from "@hotwired/stimulus"

// ── Configuration ─────────────────────────────────────────────────────────────
const CFG = {
  minR:          6,
  maxR:          44,
  spawnRate:     0.16,     // probability of spawning a drop per frame
  speed:         1.0,
  fallAmount:    0.14,
  friction:      0.22,
  spriteSize:    64,
  minRefraction: 18,
  maxRefraction: 90,
  alphaMultiply: 6,
  alphaSubtract: 3,
  brightness:    1.06,
}

function rand(a, b) { return a + Math.random() * (b - a) }
function prob(p)    { return Math.random() < p }


// ── Nighttime clouds built from overlapping soft blobs ───────────────────────
function makeClouds(ctx, w, h) {
  function hash(n) { const s = Math.sin(n) * 43758.5453; return s - Math.floor(s) }

  const horizonY = h * 0.76

  for (let i = 0; i < 6; i++) {
    const seed  = i * 1.618
    const cx    = w * hash(seed * 3.7 + 0.4)
    const cy    = horizonY * (0.08 + hash(seed * 5.1 + 1.1) * 0.62)
    const rBase = w * (0.09 + hash(seed * 7.3 + 2.2) * 0.13)
    const nBlobs = 5 + Math.round(hash(seed * 2.9) * 3)

    for (let b = 0; b < nBlobs; b++) {
      const bx = cx + (hash(b * 8.1 + seed) - 0.5) * rBase * 2.2
      const by = cy + (hash(b * 6.3 + seed + 1) - 0.5) * rBase * 0.4
      const br = rBase * (0.45 + hash(b * 11.7 + seed + 2) * 0.65)
      const a  = 0.30 + hash(b * 4.3 + seed + 3) * 0.25

      // Dark blue-grey — noticeably lighter than the near-black sky
      const g = ctx.createRadialGradient(bx, by, 0, bx, by, br)
      g.addColorStop(0,   `rgba(95, 102, 118, ${a.toFixed(3)})`)
      g.addColorStop(0.5, `rgba(82,  88, 104, ${(a * 0.45).toFixed(3)})`)
      g.addColorStop(1,   `rgba(68,  74,  90, 0)`)
      ctx.fillStyle = g
      ctx.fillRect(bx - br, by - br, br * 2, br * 2)
    }
  }
}

// ── Overcast background (dark cloudy sky, wet ground) ─────────────────────────
function makeBackground(w, h) {
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d')

  // Dark overcast sky gradient — lighter at horizon, darker overhead
  const sky = ctx.createLinearGradient(0, 0, 0, h)
  sky.addColorStop(0,   '#1a1d22')
  sky.addColorStop(0.6, '#2a2e35')
  sky.addColorStop(1,   '#353a42')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, w, h)

  return c
}

// ── Drop sprite: sphere-normal refraction map ─────────────────────────────────
// Encodes per-pixel refraction direction into a canvas matching the
// original project's waterMap texture convention:
//   R channel → y-refraction (0.5 = none, <0.5 = up, >0.5 = down)
//   G channel → x-refraction (0.5 = none, <0.5 = left, >0.5 = right)
//   B channel → depth/thickness (max at centre)
//   A channel → opacity
function makeDropSprite(size) {
  const c   = document.createElement('canvas')
  c.width   = size; c.height = size
  const ctx = c.getContext('2d')
  const img = ctx.createImageData(size, size)
  const px  = img.data
  const cx  = size / 2, cy = size / 2

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Normalised ellipse coords (slightly taller than wide)
      const dx = (x - cx) / (size * 0.38)
      const dy = (y - cy) / (size * 0.46)
      const d2 = dx * dx + dy * dy
      if (d2 >= 1) continue

      // Sphere normal via stereographic projection → converging lens refraction
      const nz = Math.sqrt(1 - d2 * 0.95)
      const nx = -dx / (1 + nz)   // negative → invert (converging lens)
      const ny = -dy / (1 + nz)

      const i = (y * size + x) * 4
      px[i]   = ((ny + 1) * 0.5 * 255) | 0             // R → y refraction
      px[i+1] = ((nx + 1) * 0.5 * 255) | 0             // G → x refraction
      px[i+2] = (Math.sqrt(1 - d2) * 255) | 0          // B → depth
      px[i+3] = (Math.pow(1 - Math.sqrt(d2), 0.4) * 255) | 0  // A → fade at edge
    }
  }
  ctx.putImageData(img, 0, 0)
  return c
}

// ── Physics simulation ────────────────────────────────────────────────────────
class RaindropSim {
  constructor(w, h, sprite) {
    this.w = w; this.h = h; this.sprite = sprite
    this.drops = []
    this.canvas = Object.assign(document.createElement('canvas'), { width: w, height: h })
    this.ctx    = this.canvas.getContext('2d')
  }

  _newDrop(x, y, r) {
    r = r ?? rand(CFG.minR, CFG.maxR * 0.5)
    return {
      x: x ?? rand(0, this.w),
      y: y ?? rand(-r * 2, -r),
      r,
      momentum:   0,
      momentumX:  rand(-0.5, 0.5),
      spreadX:    0,
      spreadY:    0,
      lastSpawn:  0,
      nextSpawn:  rand(r * 20, r * 50),
      killed:     false,
      isNew:      true,
    }
  }

  update() {
    this.ctx.clearRect(0, 0, this.w, this.h)

    if (prob(CFG.spawnRate)) this.drops.push(this._newDrop())

    const next = []
    for (const d of this.drops) {
      if (d.killed) continue

      // Larger drops accumulate momentum (gravity kicks in)
      const ratio = Math.max(0, Math.min(1, (d.r - CFG.minR) / (CFG.maxR - CFG.minR)))
      if (prob(ratio)) {
        d.momentum += CFG.speed * CFG.fallAmount * rand(0.5, 1.5) * 10
      }

      // Spawn small child drops as the parent slides (creates the trail)
      if (d.momentum > 0) {
        d.lastSpawn += d.momentum
        if (d.lastSpawn > d.nextSpawn) {
          const cr    = d.r * rand(0.2, 0.45)
          const child = this._newDrop(
            d.x + rand(-d.r * 0.1, d.r * 0.1),
            d.y - d.r * 0.01,
            cr
          )
          child.spreadY = d.momentum * 0.1
          this.drops.push(child)
          d.r        *= 0.97
          d.lastSpawn = 0
          d.nextSpawn = rand(d.r * 20, d.r * 50)
        }
      }

      d.spreadX *= 0.9
      d.spreadY *= 0.4
      d.isNew    = false

      if (d.momentum > 0) {
        d.y += d.momentum
        d.x += d.momentumX
        if (d.y > this.h + d.r) { d.killed = true; continue }

        // Absorb smaller drops nearby (collision / merging)
        for (const o of this.drops) {
          if (o === d || o.killed || d.r <= o.r) continue
          const dx = o.x - d.x, dy = o.y - d.y
          const threshold = (d.r + o.r) * (0.65 + 0.01 * d.momentum)
          if (dx * dx + dy * dy < threshold * threshold) {
            d.r         = Math.sqrt(d.r ** 2 + o.r ** 2 * 0.8)
            d.momentumX += dx * 0.01
            o.killed    = true
          }
        }
      }

      d.momentum  = Math.max(0, d.momentum - Math.max(1, CFG.minR * 0.5 - d.momentum) * CFG.friction)
      d.momentumX *= 0.7

      if (!d.killed) {
        next.push(d)
        this._draw(d)
      }
    }

    // Prevent unbounded growth when drops never leave (very wide windows)
    this.drops = next.length > 180 ? next.slice(next.length - 180) : next
  }

  _draw(d) {
    const sx = 1 + d.spreadX, sy = 1 + d.spreadY
    this.ctx.globalAlpha              = 1
    this.ctx.globalCompositeOperation = 'source-over'
    this.ctx.drawImage(
      this.sprite,
      d.x - d.r * sx,        d.y - d.r * 1.5 * sy,
      2 * d.r * sx,           2 * d.r * 1.5 * sy
    )
  }
}

// ── Shaders ───────────────────────────────────────────────────────────────────
const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`

// Fragment shader adapted from https://cs184-raineffect.web.app
// Three textures:
//   u_water — waterMap canvas (encoded refraction normals + alpha)
//   u_fg    — foreground (background scene used for refracted lookup)
//   u_bg    — background (unrefracted scene shown behind drops)
const FRAG = `
precision mediump float;

uniform sampler2D u_water;
uniform sampler2D u_fg;
uniform sampler2D u_bg;
uniform vec2  u_res;
uniform float u_time;
uniform float u_minR;
uniform float u_deltaR;
uniform float u_bright;
uniform float u_alphaMul;
uniform float u_alphaSub;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 74.3);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i+vec2(1,0)), u.x),
             mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 6; i++) { v += a * noise(p); p *= 2.1; a *= 0.5; }
  return v;
}

// UV convention in this shader: (0,0) = top-left, y increases downward.
// Returns vec2(alpha, belly) — belly is 0 at/below the cloud centre, 1 above it.
vec2 cloud(vec2 uv, float seed, float t, float aspect) {
  // Hash-derived positions and speed so clouds are independent, not linearly spaced.
  float speed = 0.010 + hash(vec2(seed, 0.3)) * 0.025;
  float baseX = hash(vec2(seed, 1.7));
  float baseY = 0.06 + hash(vec2(seed, 2.9)) * 0.62;

  float rawDy = uv.y - baseY;   // positive = below centre in this UV convention

  vec2 d = uv - vec2(fract(baseX + t * speed), baseY);
  if (d.x >  0.5) d.x -= 1.0;
  if (d.x < -0.5) d.x += 1.0;
  d.x *= aspect;
  d.y *= 2.5;

  float r  = length(d);
  vec2  nq = uv * 5.0 + vec2(t * speed * 4.0 + seed * 10.0, seed * 7.0);
  float n  = fbm(nq) * 0.18;
  float edge = 0.12 + n;
  float alpha = smoothstep(edge, edge - 0.04, r);
  float belly = smoothstep(0.05, -0.02, rawDy);  // 1 above centre, 0 below
  return vec2(alpha, belly);
}

vec4 alphaBlend(vec4 bg, vec4 fg) {
  if (fg.a == 0.0) return bg;
  if (bg.a == 1.0) return vec4(fg.a * fg.rgb + (1.0 - fg.a) * bg.rgb, 1.0);
  float ba = bg.a * (1.0 - fg.a);
  float oa = fg.a + ba;
  return vec4((fg.rgb * fg.a + bg.rgb * ba) / oa, oa);
}

void main() {
  float aspect = u_res.x / u_res.y;
  // Flip Y so UV (0,0) is top-left, matching canvas convention
  vec2 uv  = vec2(gl_FragCoord.x, u_res.y - gl_FragCoord.y) / u_res;
  vec2 px  = 1.0 / u_res;

  vec4 bg = texture2D(u_bg, uv);

  // Animated nighttime clouds — drawn over the static background, sky area only
  float skyMask = smoothstep(0.76, 0.68, uv.y);
  for (int i = 0; i < 6; i++) {
    float seed = float(i) * 1.618;
    vec2  cv   = cloud(uv, seed, u_time, aspect);
    float c    = cv.x * skyMask;
    float belly = cv.y;
    // Top of cloud is lighter, bottom is darker (shadow)
    vec3 cloudTop = vec3(0.32, 0.36, 0.45);
    vec3 cloudBot = vec3(0.14, 0.17, 0.24);
    vec3 cloudCol = mix(cloudBot, cloudTop, belly);
    bg.rgb = mix(bg.rgb, cloudCol, c * 0.85);
  }

  vec4 cur = texture2D(u_water, uv);

  float a    = clamp(cur.a * u_alphaMul - u_alphaSub, 0.0, 1.0);
  // G=x refraction, R=y refraction (0.5 = no offset, encoded in sprite)
  vec2  refr = (cur.gr - 0.5) * 2.0;
  vec2  pos  = uv + px * refr * (u_minR + cur.b * u_deltaR);

  vec4 fg     = vec4(texture2D(u_fg, pos).rgb * u_bright, a);
  vec4 result = alphaBlend(bg, fg);

  // Blinn-Phong specular — recover surface normal by negating the refraction normal
  float sNx = -(cur.g - 0.5) * 2.0;
  float sNy = -(cur.r - 0.5) * 2.0;
  float sNz = sqrt(max(0.0, 1.0 - sNx*sNx - sNy*sNy));
  vec3  N   = normalize(vec3(sNx, sNy, sNz));
  vec3  H   = normalize(vec3(-0.4, -0.7, 0.6) + vec3(0.0, 0.0, 1.0));
  float spec = pow(max(dot(N, H), 0.0), 13.0) * a;
  result.rgb += spec * 0.05;

  gl_FragColor = result;
}
`

// ── Stimulus controller ───────────────────────────────────────────────────────
export default class extends Controller {
  connect() {
    const canvas = this.element
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) return
    this._gl = gl

    this._setSize()
    const { width: w, height: h } = canvas

    const sprite = makeDropSprite(CFG.spriteSize)
    const bg     = makeBackground(w, h)
    this._sim    = new RaindropSim(w, h, sprite)

    this._prog = this._buildProg(gl)
    if (!this._prog) return

    gl.useProgram(this._prog)
    this._setupGeo(gl)
    this._setupUniforms(gl, w, h)
    this._setupTextures(gl, bg)

    this._start   = performance.now()
    this._running = true
    this._ro = new ResizeObserver(() => this._onResize())
    this._ro.observe(document.documentElement)
    this._tick()
  }

  disconnect() {
    this._running = false
    this._ro?.disconnect()
    const gl = this._gl
    if (gl) {
      if (this._prog) gl.deleteProgram(this._prog)
      if (this._buf)  gl.deleteBuffer(this._buf)
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  _setSize() {
    const c = this.element
    c.width  = window.innerWidth
    c.height = window.innerHeight
    if (this._gl) this._gl.viewport(0, 0, c.width, c.height)
  }

  _onResize() {
    this._setSize()
    const w = this.element.width, h = this.element.height
    if (!this._sim) return
    this._sim.w = w; this._sim.h = h
    this._sim.canvas.width  = w
    this._sim.canvas.height = h

    const bg = makeBackground(w, h)
    const gl = this._gl
    gl.useProgram(this._prog)
    gl.uniform2f(this._u.res, w, h)
    ;[['fg', 1], ['bg', 2]].forEach(([k, unit]) => {
      gl.activeTexture(gl.TEXTURE0 + unit)
      gl.bindTexture(gl.TEXTURE_2D, this._tex[k])
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bg)
    })
  }

  _tick() {
    if (!this._running) return
    this._sim.update()

    const gl = this._gl
    gl.useProgram(this._prog)
    gl.uniform1f(this._u.time, (performance.now() - this._start) / 1000)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this._tex.water)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this._sim.canvas)
    gl.drawArrays(gl.TRIANGLES, 0, 6)

    requestAnimationFrame(() => this._tick())
  }

  _buildProg(gl) {
    const vs = this._compile(gl, gl.VERTEX_SHADER,   VERT)
    const fs = this._compile(gl, gl.FRAGMENT_SHADER, FRAG)
    if (!vs || !fs) return null
    const p = gl.createProgram()
    gl.attachShader(p, vs); gl.attachShader(p, fs)
    gl.linkProgram(p)
    gl.deleteShader(vs); gl.deleteShader(fs)
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.error('Rain shader link error:', gl.getProgramInfoLog(p))
      return null
    }
    return p
  }

  _compile(gl, type, src) {
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error('Rain shader compile error:', gl.getShaderInfoLog(s))
      gl.deleteShader(s); return null
    }
    return s
  }

  _setupGeo(gl) {
    this._buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this._buf)
    // Two triangles covering clip space
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1,-1, 1,-1, -1,1,  -1,1, 1,-1, 1,1]),
      gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(this._prog, 'a_pos')
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
  }

  _setupUniforms(gl, w, h) {
    const loc = n => gl.getUniformLocation(this._prog, n)
    this._u = {
      res:      loc('u_res'),
      time:     loc('u_time'),
      minR:     loc('u_minR'),
      deltaR:   loc('u_deltaR'),
      bright:   loc('u_bright'),
      alphaMul: loc('u_alphaMul'),
      alphaSub: loc('u_alphaSub'),
    }
    gl.uniform2f(this._u.res,      w, h)
    gl.uniform1f(this._u.minR,     CFG.minRefraction)
    gl.uniform1f(this._u.deltaR,   CFG.maxRefraction - CFG.minRefraction)
    gl.uniform1f(this._u.bright,   CFG.brightness)
    gl.uniform1f(this._u.alphaMul, CFG.alphaMultiply)
    gl.uniform1f(this._u.alphaSub, CFG.alphaSubtract)
    gl.uniform1i(gl.getUniformLocation(this._prog, 'u_water'), 0)
    gl.uniform1i(gl.getUniformLocation(this._prog, 'u_fg'),    1)
    gl.uniform1i(gl.getUniformLocation(this._prog, 'u_bg'),    2)
  }

  _setupTextures(gl, bg) {
    const mkTex = (unit, src) => {
      gl.activeTexture(gl.TEXTURE0 + unit)
      const t = gl.createTexture()
      gl.bindTexture(gl.TEXTURE_2D, t)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S,     gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T,     gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      if (src) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
      return t
    }
    this._tex = {
      water: mkTex(0, null),
      fg:    mkTex(1, bg),
      bg:    mkTex(2, bg),
    }
  }
}
